import React, { useState } from 'react';
import { Activity, Calendar, TrendingUp, AlertCircle, Loader, Play, ChevronDown, ChevronRight, Download, CheckCircle2 } from 'lucide-react';
import { ResponsiveContainer, CartesianGrid, XAxis, YAxis, Tooltip, ReferenceLine, BarChart, Bar, Cell, ComposedChart, Area, Line } from 'recharts';
import { useApi } from '../hooks/useApi';
import { COLLAPSE_MODELS, SPIKE_MODELS } from './LivePreds';

const MODELS = ['v3.1.0', 'v9.0.0-daily', 'v11-tabpfn-v2', 'v11.0.0-tabpfn-3.5-daily-gpu', 'v20.0.0', 'v30-k', 'v32-k-id'];
const QUANTILES = ['q05', 'q10', 'q20', 'q30', 'q40', 'q50', 'q60', 'q70', 'q80', 'q90', 'q95'];

// Each probabilistic model trades with its own upshift; the downshift is shared.
// Picking a model selects its upshift so two models are never compared on one
// model's spike engine by accident.
const PAIRED_UPSHIFT = {
  'v3.1.0': 'prod-spike-r3-champion',
  'v9.0.0-daily': 'v9.0.0-upshift',
  'v11-tabpfn-v2': 'v9.0.0-upshift',
  'v11.0.0-tabpfn-3.5-daily-gpu': 'v9.0.0-upshift',
  'v20.0.0': 'v9.0.0-upshift',
  'v30-k': 'v9.0.0-upshift',
  'v32-k-id': 'v9.0.0-upshift',
};

// Chart series colors, checked as a pair for colorblind separation and contrast
// on both the light and the dark card surface.
const BLUE = '#2a78d6';
const ORANGE = '#d95926';
const GRID = '#e5e7eb';
// The dashed curve of the forecast without shifts on a day's graph.
const UNSHIFTED = '#94a3b8';

const PAGE_SIZE = 31;

const STATUS = {
  settled: { label: 'Settled', cls: 'bg-green-100 text-green-700' },
  pending: { label: 'Awaiting Fix II', cls: 'bg-amber-100 text-amber-700' },
  missing_forecast: { label: 'No forecast', cls: 'bg-red-100 text-red-700' },
  incomplete_forecast: { label: 'Incomplete forecast', cls: 'bg-red-100 text-red-700' },
};

// Rounded before formatting so a tiny negative never prints as "-0.00".
const rounded = (v, digits) => Math.round(v * 10 ** digits) / 10 ** digits + 0;
const fmt = (v, digits = 2) => (v == null || isNaN(v)
  ? '—'
  : rounded(v, digits).toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits }));
const signed = (v, digits = 2) => (v == null || isNaN(v) ? '—' : `${rounded(v, digits) > 0 ? '+' : ''}${fmt(v, digits)}`);
const pct = (v) => (v == null ? '—' : `${fmt(v * 100, 1)}%`);

const formatDate = (date) => {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

const downloadCsv = (name, rows) => {
  if (!rows.length) return;
  const keys = Object.keys(rows[0]);
  const escape = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
  const csv = [keys.join(','), ...rows.map(row => keys.map(k => escape(row[k])).join(','))].join('\r\n');
  const url = URL.createObjectURL(new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8;' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const Metric = ({ label, value, hint }) => (
  <div className="bg-white rounded-lg p-4 border border-gray-200">
    <p className="text-xs text-gray-500 uppercase">{label}</p>
    <p className="text-2xl font-bold text-gray-800 mt-1">{value}</p>
    {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
  </div>
);

const Panel = ({ title, subtitle, action, children }) => (
  <div className="bg-white border border-gray-200 rounded-lg">
    <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between gap-4">
      <div>
        <h3 className="font-semibold text-gray-800">{title}</h3>
        {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
      </div>
      {action}
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const Toggle = ({ label, checked, onChange }) => (
  <label className="flex items-center space-x-3 cursor-pointer">
    <span className="text-sm text-gray-700">{label}</span>
    <div className="relative inline-flex items-center">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="sr-only peer" />
      <div className="w-11 h-6 bg-gray-200 border border-gray-400 shadow-sm peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:border-blue-500 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[1px] after:left-[1px] after:bg-white after:border after:border-gray-400 after:shadow-sm after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
    </div>
  </label>
);

// kind: 'bar' (square swatch), 'line', 'dash' or 'band'.
const ChartLegend = ({ items }) => (
  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600 mb-2">
    {items.map(({ label, color, kind = 'bar' }) => (
      <span key={label} className="inline-flex items-center gap-1.5">
        {kind === 'bar' && <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: color }} />}
        {kind === 'band' && <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: color, opacity: 0.3 }} />}
        {kind === 'line' && <span className="inline-block w-4" style={{ borderTop: `2px solid ${color}` }} />}
        {kind === 'dash' && <span className="inline-block w-4" style={{ borderTop: `2px dashed ${color}` }} />}
        {label}
      </span>
    ))}
  </div>
);

const ShiftChoice = ({ withShifts, onChange }) => (
  <div
    role="group"
    aria-label="Show with or without shifts"
    className="inline-flex shrink-0 rounded-lg border border-gray-300 overflow-hidden text-xs font-medium"
  >
    {[[true, 'With shifts'], [false, 'No shifts']].map(([value, label]) => (
      <button
        key={label}
        type="button"
        aria-pressed={withShifts === value}
        onClick={() => onChange(value)}
        className={`px-2.5 py-1 ${withShifts === value ? 'bg-blue-600 text-white' : 'bg-white text-gray-600'}`}
      >
        {label}
      </button>
    ))}
  </div>
);

// Every panel whose figures depend on the shifts keeps its own "With shifts / No shifts"
// choice. Returns [withShifts, control, shifted]; a run without any shift has one
// version only, so there is no control to show.
const useShiftChoice = (result) => {
  const [chosen, setChosen] = useState(true);
  const shifted = result.parameters.apply_risk_score || result.parameters.apply_spike_risk;
  const withShifts = chosen || !shifted;
  const control = shifted ? <ShiftChoice withShifts={withShifts} onChange={setChosen} /> : null;
  return [withShifts, control, shifted];
};

const shiftLabel = (withShifts) => (withShifts ? 'with shifts' : 'no shifts');
// Subtitle suffix naming the version a panel is showing.
const versionNote = (shifted, withShifts) => (shifted ? ` · ${shiftLabel(withShifts)}` : '');

// Every quantile of an hour carries the same shift, so taking it off again gives the forecast as issued.
const hourShift = (h) => (h.down_shift || 0) + (h.up_shift || 0);

// The lowest and highest value across both versions of a graph. Drawn as invisible
// marks that extend the y-axis, they keep the scale still when the choice flips,
// so only the data moves.
const axisPins = (values) => {
  const present = values.filter(v => v != null);
  return present.length ? [Math.min(...present), Math.max(...present)] : [];
};
const renderAxisPins = (pins) => pins.map((y, index) => (
  <ReferenceLine key={index} y={y} stroke="none" strokeOpacity={0} ifOverflow="extendDomain" />
));

const axisTick = { fontSize: 12 };
const chartMargin = { top: 8, right: 12, left: 0, bottom: 0 };
const hoverCursor = { fill: 'rgba(148, 163, 184, 0.15)' };

const th = 'px-3 py-2 text-right font-medium';
const td = 'px-3 py-1.5 text-right';
const headRow = 'text-gray-600 border-b border-gray-200';

const SummaryTiles = ({ result }) => {
  const [withShifts, control, shifted] = useShiftChoice(result);
  const d = withShifts ? result.diagnosis : result.raw_diagnosis;
  const other = withShifts ? result.raw_diagnosis : result.diagnosis;
  // Each tile also names the other version's figure, so the difference shows without flipping.
  const also = (text) => (shifted ? `${shiftLabel(!withShifts)} ${text}` : null);
  const join = (...parts) => parts.filter(Boolean).join(' · ');
  return (
    <div>
      <div className="flex items-center justify-between gap-4 mb-2">
        <h3 className="font-semibold text-gray-800">
          Summary<span className="text-xs font-normal text-gray-500">{versionNote(shifted, withShifts)}</span>
        </h3>
        {control}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Metric label="Hourly MAE" value={fmt(d.hourly.mae)} hint={join(`${fmt(d.hourly.n, 0)} scored hours`, also(fmt(other.hourly.mae)))} />
        <Metric label="Hourly RMSE" value={fmt(d.hourly.rmse)} hint={join('PLN/MWh', also(fmt(other.hourly.rmse)))} />
        <Metric label="Hourly bias" value={signed(d.hourly.bias)} hint={join('Forecast − Fix II', also(signed(other.hourly.bias)))} />
        <Metric
          label="Direction vs TGE BASE"
          value={pct(d.directional_accuracy)}
          hint={join(`against Fix I · ${d.directional_days} days with a BASE price`, also(pct(other.directional_accuracy)))}
        />
      </div>
    </div>
  );
};

const AccuracyPanel = ({ result }) => {
  const [withShifts, control, shifted] = useShiftChoice(result);
  const d = withShifts ? result.diagnosis : result.raw_diagnosis;
  return (
    <Panel
      title="Forecast accuracy"
      subtitle={`The hourly curve and the daily average are scored separately${versionNote(shifted, withShifts)}`}
      action={control}
    >
      <table className="min-w-full text-sm">
        <thead>
          <tr className={headRow}>
            <th className="px-3 py-2 text-left font-medium">Metric</th>
            <th className={th}>Hourly</th>
            <th className={th}>Daily average</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {[['Observations', 'n', 0], ['MAE · PLN/MWh', 'mae'], ['RMSE · PLN/MWh', 'rmse'], ['Bias · PLN/MWh', 'bias'], ['Correlation', 'correlation', 3], ['R²', 'r2', 3]].map(([label, key, digits = 2]) => (
            <tr key={key}>
              <td className="px-3 py-1.5 text-gray-700">{label}</td>
              <td className={`${td} text-gray-800`}>{fmt(d.hourly[key], digits)}</td>
              <td className={`${td} text-gray-800`}>{fmt(d.daily[key], digits)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Panel>
  );
};

const CalibrationPanel = ({ result }) => {
  const [withShifts, control, shifted] = useShiftChoice(result);
  const d = withShifts ? result.diagnosis : result.raw_diagnosis;
  return (
    <Panel
      title="Quantile calibration"
      subtitle={`Share of scored hours where Fix II landed inside the forecast interval${versionNote(shifted, withShifts)}`}
      action={control}
    >
      <table className="min-w-full text-sm">
        <thead>
          <tr className={headRow}>
            <th className="px-3 py-2 text-left font-medium">Interval</th>
            <th className={th}>Target</th>
            <th className={th}>Observed</th>
            <th className={th}>Gap</th>
            <th className={th}>Avg width</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {d.intervals.filter(i => i.n > 0).map(i => (
            <tr key={i.low}>
              <td className="px-3 py-1.5 text-gray-700">{i.low}–{i.high}</td>
              <td className={`${td} text-gray-600`}>{pct(i.target)}</td>
              <td className={`${td} font-semibold text-gray-900`}>{pct(i.coverage)}</td>
              <td className={`${td} text-gray-600`}>{signed((i.coverage - i.target) * 100, 1)} pp</td>
              <td className={`${td} text-gray-600`}>{fmt(i.width)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-xs text-gray-500 mt-3">
        Mean pinball loss <b className="text-gray-700">{fmt(d.pinball)}</b> over {fmt(d.pinball_cells, 0)} quantile cells. A positive gap means the interval is wider than it needs to be.
      </p>
    </Panel>
  );
};

const HourlyAccuracyPanel = ({ result, displayHour }) => {
  const [withShifts, control, shifted] = useShiftChoice(result);
  const byHour = (withShifts ? result.diagnosis : result.raw_diagnosis).by_hour;
  const data = byHour.map(h => ({ ...h, label: displayHour(h.hour) }));
  const pins = shifted
    ? axisPins([...result.diagnosis.by_hour, ...result.raw_diagnosis.by_hour].flatMap(h => [h.mae, h.bias]))
    : [];
  return (
    <Panel title="Accuracy by delivery hour" subtitle={`MAE and bias · PLN/MWh${versionNote(shifted, withShifts)}`} action={control}>
      <ChartLegend items={[{ label: 'MAE', color: BLUE }, { label: 'Bias (forecast − Fix II)', color: ORANGE }]} />
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={chartMargin} barGap={2}>
            <CartesianGrid vertical={false} stroke={GRID} />
            <XAxis dataKey="label" tick={axisTick} tickLine={false} />
            <YAxis width={56} tick={axisTick} tickLine={false} axisLine={false} tickFormatter={v => fmt(v, 0)} />
            <Tooltip cursor={hoverCursor} labelFormatter={label => `Hour ${label}`} formatter={(value, name) => [`${fmt(value)} PLN/MWh`, name]} />
            <ReferenceLine y={0} stroke="#94a3b8" />
            {renderAxisPins(pins)}
            <Bar name="MAE" dataKey="mae" fill={BLUE} radius={[4, 4, 0, 0]} maxBarSize={24} isAnimationActive={false} />
            <Bar name="Bias" dataKey="bias" fill={ORANGE} radius={[4, 4, 0, 0]} maxBarSize={24} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Panel>
  );
};

const DailyErrorPanel = ({ result }) => {
  const [withShifts, control, shifted] = useShiftChoice(result);
  const { days } = result;
  const errorKey = withShifts ? 'error' : 'raw_error';
  const pins = shifted ? axisPins(days.flatMap(d => [d.error, d.raw_error])) : [];
  return (
    <Panel title="Daily average error" subtitle={`Forecast avg − Fix II avg · PLN/MWh${versionNote(shifted, withShifts)}`} action={control}>
      <ChartLegend items={[{ label: 'Forecast above Fix II', color: BLUE }, { label: 'Forecast below Fix II', color: ORANGE }]} />
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={days} margin={chartMargin}>
            <CartesianGrid vertical={false} stroke={GRID} />
            <XAxis dataKey="date" tick={axisTick} tickLine={false} tickFormatter={v => v.slice(5)} minTickGap={35} />
            <YAxis width={56} tick={axisTick} tickLine={false} axisLine={false} tickFormatter={v => fmt(v, 0)} />
            <Tooltip cursor={hoverCursor} formatter={value => [`${signed(value)} PLN/MWh`, 'Forecast − Fix II']} />
            <ReferenceLine y={0} stroke="#94a3b8" />
            {renderAxisPins(pins)}
            <Bar dataKey={errorKey} radius={[4, 4, 0, 0]} maxBarSize={24} isAnimationActive={false}>
              {days.map(d => <Cell key={d.date} fill={(d[errorKey] || 0) >= 0 ? BLUE : ORANGE} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Panel>
  );
};

// Both versions side by side, so this one needs no choice.
const ShiftImpactPanel = ({ result }) => {
  const { diagnosis: d, raw_diagnosis: raw } = result;
  const change = (after, before) => (after == null || before == null ? null : after - before);
  return (
    <Panel title="Shift impact" subtitle="The same hours and days scored without and with the shifts.">
      <table className="min-w-full text-sm">
        <thead>
          <tr className={headRow}>
            <th className="px-3 py-2 text-left font-medium">Forecast</th>
            <th className={th}>Hourly MAE</th>
            <th className={th}>Hourly RMSE</th>
            <th className={th}>Hourly bias</th>
            <th className={th}>Daily MAE</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {[['No shifts', raw], ['With shifts', d]].map(([label, version]) => (
            <tr key={label}>
              <td className="px-3 py-1.5 text-gray-700">{label}</td>
              <td className={`${td} text-gray-800`}>{fmt(version.hourly.mae)}</td>
              <td className={`${td} text-gray-800`}>{fmt(version.hourly.rmse)}</td>
              <td className={`${td} text-gray-800`}>{signed(version.hourly.bias)}</td>
              <td className={`${td} text-gray-800`}>{fmt(version.daily.mae)}</td>
            </tr>
          ))}
          <tr>
            <td className="px-3 py-1.5 font-medium text-gray-700">Change</td>
            <td className={`${td} font-medium text-gray-800`}>{signed(change(d.hourly.mae, raw.hourly.mae))}</td>
            <td className={`${td} font-medium text-gray-800`}>{signed(change(d.hourly.rmse, raw.hourly.rmse))}</td>
            <td className={`${td} font-medium text-gray-800`}>{signed(change(d.hourly.bias, raw.hourly.bias))}</td>
            <td className={`${td} font-medium text-gray-800`}>{signed(change(d.daily.mae, raw.daily.mae))}</td>
          </tr>
        </tbody>
      </table>
      <p className="text-xs text-gray-500 mt-3">
        Downshift active on {fmt(d.down_shift_hours, 0)} hours · upshift active on {fmt(d.up_shift_hours, 0)} hours.
      </p>
    </Panel>
  );
};

const BaseBenchmarkPanel = ({ result }) => {
  const [withShifts, control, shifted] = useShiftChoice(result);
  const d = withShifts ? result.diagnosis : result.raw_diagnosis;
  return (
    <Panel
      title="Model versus TGE BASE"
      subtitle={`Daily average against Fix I, on the days that have a BASE price${versionNote(shifted, withShifts)}`}
      action={control}
    >
      <table className="min-w-full text-sm">
        <thead>
          <tr className={headRow}>
            <th className="px-3 py-2 text-left font-medium">Daily average benchmark</th>
            <th className={th}>Days</th>
            <th className={th}>MAE</th>
            <th className={th}>RMSE</th>
            <th className={th}>Bias</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {[['Selected model', d.model_on_base_days], ['TGE BASE', d.base_benchmark]].map(([label, row]) => (
            <tr key={label}>
              <td className="px-3 py-1.5 text-gray-700">{label}</td>
              <td className={`${td} text-gray-800`}>{row.n}</td>
              <td className={`${td} text-gray-800`}>{fmt(row.mae)}</td>
              <td className={`${td} text-gray-800`}>{fmt(row.rmse)}</td>
              <td className={`${td} text-gray-800`}>{signed(row.bias)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-xs text-gray-500 mt-3">
        Direction, right on <b className="text-gray-700">{pct(d.directional_accuracy)}</b> of these days: the forecast average and the Fix I average fall on the same side of the BASE price.
        This panel and the direction are measured against Fix I, unlike the rest of the page. BASE clears after the forecast is issued, so it is a benchmark here, not an input.
      </p>
    </Panel>
  );
};

const MonthlyPanel = ({ result }) => {
  const [withShifts, control, shifted] = useShiftChoice(result);
  const monthly = withShifts ? result.monthly : result.raw_monthly;
  return (
    <Panel
      title="Monthly diagnosis"
      subtitle={`Daily average and hourly forecast errors · PLN/MWh${versionNote(shifted, withShifts)}`}
      action={control}
    >
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className={headRow}>
              <th className="px-3 py-2 text-left font-medium">Month</th>
              <th className={th}>Scored days</th>
              <th className={th}>Daily MAE</th>
              <th className={th}>Daily RMSE</th>
              <th className={th}>Daily bias</th>
              <th className={th}>Hourly MAE</th>
              <th className={th}>Hourly bias</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {monthly.map(m => (
              <tr key={m.month}>
                <td className="px-3 py-1.5 text-gray-700">{m.month}</td>
                <td className={`${td} text-gray-800`}>{m.accuracy.n}</td>
                <td className={`${td} text-gray-800`}>{fmt(m.accuracy.mae)}</td>
                <td className={`${td} text-gray-800`}>{fmt(m.accuracy.rmse)}</td>
                <td className={`${td} text-gray-800`}>{signed(m.accuracy.bias)}</td>
                <td className={`${td} text-gray-800`}>{fmt(m.hourly.mae)}</td>
                <td className={`${td} text-gray-800`}>{signed(m.hourly.bias)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
};

// An expanded day shows both versions at once - the shifted forecast with the unshifted
// one beside it - so it has no "With shifts / No shifts" choice of its own.
const DayForecastChart = ({ day, shifted, displayHour }) => {
  const dayShifted = day.down_shift_hours + day.up_shift_hours > 0;
  const data = day.hourly.map(h => ({
    label: displayHour(h.hour),
    actual: h.actual,
    q50: h.q50,
    unshifted: h.raw_q50,
    band: h.q10 != null && h.q90 != null ? [h.q10, h.q90] : null,
  }));
  const name = `Forecast q50${versionNote(shifted, true)}`;
  const unshiftedName = 'Forecast q50 · no shifts';
  return (
    <>
      <ChartLegend
        items={[
          { label: name, color: BLUE, kind: 'line' },
          ...(dayShifted ? [{ label: unshiftedName, color: UNSHIFTED, kind: 'dash' }] : []),
          { label: 'Fix II', color: ORANGE, kind: 'line' },
          { label: `q10–q90${versionNote(shifted, true)}`, color: BLUE, kind: 'band' },
        ]}
      />
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={chartMargin}>
            <CartesianGrid vertical={false} stroke={GRID} />
            <XAxis dataKey="label" tick={axisTick} tickLine={false} />
            <YAxis width={56} tick={axisTick} tickLine={false} axisLine={false} tickFormatter={v => fmt(v, 0)} />
            <Tooltip
              labelFormatter={label => `Hour ${label}`}
              formatter={(value, name) => [Array.isArray(value) ? value.map(v => fmt(v)).join(' – ') : fmt(value), name]}
            />
            <Area name="q10–q90" dataKey="band" stroke="none" fill={BLUE} fillOpacity={0.18} activeDot={false} connectNulls={false} isAnimationActive={false} />
            {dayShifted && (
              <Line name={unshiftedName} dataKey="unshifted" stroke={UNSHIFTED} strokeWidth={1.5} strokeDasharray="4 3" dot={false} isAnimationActive={false} />
            )}
            <Line name={name} dataKey="q50" stroke={BLUE} strokeWidth={2} dot={false} isAnimationActive={false} />
            <Line name="Fix II" dataKey="actual" stroke={ORANGE} strokeWidth={2} dot={false} isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </>
  );
};

// Tighter cells than the other tables: with shifts on, the hourly table has 20 columns.
const hourTh = 'px-2 py-2 text-right font-medium';
const hourTd = 'px-2 py-1.5 text-right';

const DayDetail = ({ day, parameters, displayHour }) => {
  const down = parameters.apply_risk_score;
  const up = parameters.apply_spike_risk;
  const shifted = down || up;
  const anyShift = day.down_shift_hours + day.up_shift_hours > 0;
  const unshiftedError = (h) => (h.raw_q50 != null && h.actual != null ? h.raw_q50 - h.actual : null);
  // The figure without shifts, named in a tile's small print on days where it differs.
  const noShifts = (text) => (anyShift ? `no shifts ${text}` : null);
  const join = (...parts) => parts.filter(Boolean).join(' · ');

  const exportHours = () => downloadCsv(`diagnosis-hours-${parameters.model}-${day.date}.csv`, day.hourly.map(h => ({
    hour: displayHour(h.hour),
    ...Object.fromEntries(QUANTILES.map(q => [q, h[q]])),
    q50_no_shifts: h.raw_q50,
    down_shift: h.down_shift,
    down_risk: h.down_risk,
    up_shift: h.up_shift,
    up_risk: h.up_risk,
    fix_ii: h.actual,
    error: h.error,
    error_no_shifts: unshiftedError(h),
  })));

  return (
    <div className="px-2 py-3 space-y-4">
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-500">
        <span>Delivery hours: <b className="text-gray-700">{day.hours}</b></span>
        <span>Forecast coverage: <b className="text-gray-700">{day.forecast_hours}/{day.hours}</b></span>
        <span>Fix II coverage: <b className="text-gray-700">{day.actual_hours}/{day.hours}</b></span>
        {anyShift && <span>Shifted hours: <b className="text-gray-700">{day.down_shift_hours} down · {day.up_shift_hours} up</b></span>}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Metric label="Forecast avg" value={fmt(day.forecast)} hint={noShifts(fmt(day.raw_forecast)) || 'PLN/MWh'} />
        <Metric label="Fix II avg" value={fmt(day.settlement)} hint={`TGE BASE ${fmt(day.base_price)}`} />
        <Metric label="Daily error" value={signed(day.error)} hint={join('Forecast − Fix II', noShifts(signed(day.raw_error)))} />
        <Metric
          label="Hourly MAE / RMSE"
          value={`${fmt(day.hourly_metrics.mae)} / ${fmt(day.hourly_metrics.rmse)}`}
          hint={join(`${day.hourly_metrics.n} scored hours`, noShifts(`${fmt(day.raw_hourly_metrics.mae)} / ${fmt(day.raw_hourly_metrics.rmse)}`))}
        />
      </div>

      {day.hours !== 24 && (
        <p className="text-xs text-gray-500">Daylight-saving day: {day.hours} delivery hours.</p>
      )}

      <DayForecastChart day={day} shifted={shifted} displayHour={displayHour} />

      <div className="flex items-center justify-between text-xs text-gray-500">
        <span className={anyShift ? 'text-amber-700' : ''}>
          {anyShift ? 'Quantiles include the applied shifts — hover a cell for the value without them' : 'No shift applied on this day'}
        </span>
        <button onClick={exportHours} className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 font-medium">
          <Download size={14} />Export hourly data
        </button>
      </div>

      <div className="overflow-x-auto border border-gray-200 rounded-lg bg-white">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-gray-100 text-gray-600">
              <th className="px-2 py-2 text-left font-medium">Hour</th>
              {QUANTILES.map(q => (
                <th key={q} className={`${hourTh} ${q === 'q50' ? 'text-gray-900' : ''}`}>{q.toUpperCase()}</th>
              ))}
              {down && <><th className={hourTh}>Down Shift</th><th className={hourTh}>Down Risk</th></>}
              {up && <><th className={hourTh}>Up Shift</th><th className={hourTh}>Up Risk</th></>}
              {shifted && <th className={hourTh}>Q50 No Shifts</th>}
              <th className={hourTh}>Fix II</th>
              <th className={hourTh}>Error</th>
              {shifted && <th className={hourTh}>Error No Shifts</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {day.hourly.map(h => {
              const shift = hourShift(h);
              return (
                <tr key={h.hour}>
                  <td className="px-2 py-1.5 font-mono text-gray-700">{displayHour(h.hour)}</td>
                  {QUANTILES.map(q => (
                    <td
                      key={q}
                      title={h[q] != null && shift !== 0 ? `no shifts ${fmt(h[q] - shift)}` : undefined}
                      className={`${hourTd} ${q === 'q50' ? 'font-semibold text-gray-900' : 'text-gray-600'} ${shift !== 0 ? 'underline decoration-dotted decoration-gray-300 underline-offset-4' : ''}`}
                    >
                      {fmt(h[q])}
                    </td>
                  ))}
                  {down && (
                    <>
                      <td className={`${hourTd} ${h.down_shift < 0 ? 'text-red-600 font-medium' : 'text-gray-400'}`}>{h.down_shift != null ? fmt(h.down_shift) : '-'}</td>
                      <td className={`${hourTd} ${h.down_risk < 0 ? 'text-red-600 font-medium' : 'text-gray-400'}`}>{h.down_risk != null ? fmt(h.down_risk) : '-'}</td>
                    </>
                  )}
                  {up && (
                    <>
                      <td className={`${hourTd} ${h.up_shift > 0 ? 'text-amber-700 font-medium' : 'text-gray-400'}`}>{h.up_shift != null ? fmt(h.up_shift) : '-'}</td>
                      <td className={`${hourTd} ${h.up_risk > 0 ? 'text-amber-700 font-medium' : 'text-gray-400'}`}>{h.up_risk != null ? fmt(h.up_risk) : '-'}</td>
                    </>
                  )}
                  {shifted && <td className={`${hourTd} text-gray-600`}>{fmt(h.raw_q50)}</td>}
                  <td className={`${hourTd} text-gray-800`}>{fmt(h.actual)}</td>
                  <td className={`${hourTd} text-gray-800`}>{signed(h.error)}</td>
                  {shifted && <td className={`${hourTd} text-gray-600`}>{signed(unshiftedError(h))}</td>}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const DailyEntries = ({ result, displayHour }) => {
  const { days, parameters } = result;
  const [withShifts, control, shifted] = useShiftChoice(result);
  const [expanded, setExpanded] = useState({});
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(0);

  const matches = (d) => (
    filter === 'all'
    || (filter === 'missing' ? d.status.endsWith('forecast')
      : filter === 'shifted' ? d.down_shift_hours + d.up_shift_hours > 0
      : d.status === filter)
  );
  const filtered = [...days].reverse().filter(matches);
  const displayed = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const columns = shifted ? 8 : 7;
  const toggle = (date) => setExpanded(old => ({ ...old, [date]: !old[date] }));

  // The export carries both versions, whatever the panel is showing.
  const exportDays = () => downloadCsv(
    `diagnosis-days-${parameters.model}.csv`,
    days.map(({ hourly, hourly_metrics, raw_hourly_metrics, ...day }) => ({
      ...day,
      hourly_mae: hourly_metrics.mae,
      hourly_rmse: hourly_metrics.rmse,
      raw_hourly_mae: raw_hourly_metrics.mae,
      raw_hourly_rmse: raw_hourly_metrics.rmse,
    })),
  );

  return (
    <Panel
      title="Daily entries"
      subtitle={shifted
        ? `The list is shown ${shiftLabel(withShifts)} · an expanded day shows its price curve and hours both ways`
        : 'Expand a day to inspect the price curve and the forecast quantiles.'}
      action={(
        <div className="flex items-center gap-4">
          {control}
          <button onClick={exportDays} className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 font-medium whitespace-nowrap">
            <Download size={15} />Export days
          </button>
        </div>
      )}
    >
      <div className="flex flex-wrap items-center gap-4 mb-3 text-sm text-gray-600">
        <label className="inline-flex items-center gap-2">
          Show
          <select
            aria-label="Filter days"
            value={filter}
            onChange={(e) => { setFilter(e.target.value); setPage(0); }}
            className="px-2 py-1 border border-gray-300 rounded-lg"
          >
            <option value="all">All days</option>
            <option value="settled">Settled</option>
            <option value="pending">Awaiting Fix II</option>
            <option value="missing">Missing / incomplete forecast</option>
            {shifted && <option value="shifted">Days with a shift</option>}
          </select>
        </label>
        <span>{filtered.length} days · latest first</span>
        <button onClick={() => setExpanded(Object.fromEntries(displayed.map(d => [d.date, true])))} className="text-blue-600 hover:text-blue-800 font-medium">Expand page</button>
        <button onClick={() => setExpanded({})} className="text-blue-600 hover:text-blue-800 font-medium">Collapse all</button>
      </div>

      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        {/* Fixed layout: an expanded day must scroll inside its own row, not widen this table. */}
        <table className="w-full min-w-[900px] table-fixed text-sm">
          <thead>
            <tr className="bg-gray-100 text-gray-600">
              <th className="w-52 px-3 py-2 text-left font-medium">Delivery day</th>
              <th className={th}>Forecast avg</th>
              <th className={th}>Fix II avg</th>
              <th className={th}>TGE BASE</th>
              <th className={th}>Daily error</th>
              <th className={th}>Hourly MAE</th>
              {shifted && <th className={th}>Shifted hours</th>}
              <th className="px-3 py-2 text-left font-medium">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {displayed.map(day => (
              <React.Fragment key={day.date}>
                <tr
                  className="data-row cursor-pointer"
                  role="button"
                  tabIndex={0}
                  aria-expanded={!!expanded[day.date]}
                  onClick={() => toggle(day.date)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(day.date); } }}
                >
                  <td className="px-3 py-2 text-gray-800 whitespace-nowrap">
                    <span className="inline-flex items-center gap-2">
                      {expanded[day.date] ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronRight size={16} className="text-gray-400" />}
                      <span className="font-medium">{day.date}</span>
                      <span className="text-xs text-gray-400">{day.weekday}{day.hours !== 24 ? ` · ${day.hours} h` : ''}</span>
                    </span>
                  </td>
                  <td className={`${td} text-gray-800`}>{fmt(withShifts ? day.forecast : day.raw_forecast)}</td>
                  <td className={`${td} text-gray-800`}>{fmt(day.settlement)}</td>
                  <td className={`${td} text-gray-600`}>{fmt(day.base_price)}</td>
                  <td className={`${td} text-gray-800`}>{signed(withShifts ? day.error : day.raw_error)}</td>
                  <td className={`${td} text-gray-800`}>{fmt((withShifts ? day.hourly_metrics : day.raw_hourly_metrics).mae)}</td>
                  {shifted && (
                    <td className={`${td} text-gray-600`}>
                      {day.down_shift_hours + day.up_shift_hours > 0 ? `${day.down_shift_hours} down · ${day.up_shift_hours} up` : '—'}
                    </td>
                  )}
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS[day.status].cls}`}>{STATUS[day.status].label}</span>
                  </td>
                </tr>
                {expanded[day.date] && (
                  <tr>
                    <td colSpan={columns} className="bg-gray-50">
                      <DayDetail day={day} parameters={parameters} displayHour={displayHour} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {!displayed.length && (
              <tr><td colSpan={columns} className="px-3 py-8 text-center text-gray-500">No days match this filter.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-end gap-3 mt-3 text-sm text-gray-600">
        <span>Page {page + 1} of {Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))}</span>
        <button disabled={page === 0} onClick={() => setPage(p => p - 1)} className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-40">Previous</button>
        <button disabled={(page + 1) * PAGE_SIZE >= filtered.length} onClick={() => setPage(p => p + 1)} className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-40">Next</button>
      </div>
    </Panel>
  );
};

const DiagnosisResults = ({ result, displayHour }) => {
  const shifted = result.parameters.apply_risk_score || result.parameters.apply_spike_risk;

  if (result.diagnosis.hourly.n === 0) {
    return (
      <>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
          No hour in this period has both a forecast and a Fix II price yet, so there is nothing to score.
        </div>
        <DailyEntries result={result} displayHour={displayHour} />
      </>
    );
  }

  return (
    <>
      <SummaryTiles result={result} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <AccuracyPanel result={result} />
        <CalibrationPanel result={result} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <HourlyAccuracyPanel result={result} displayHour={displayHour} />
        <DailyErrorPanel result={result} />
      </div>

      <div className={`grid grid-cols-1 gap-4 ${shifted ? 'lg:grid-cols-2' : ''}`}>
        {shifted && <ShiftImpactPanel result={result} />}
        <BaseBenchmarkPanel result={result} />
      </div>

      <MonthlyPanel result={result} />

      <DailyEntries result={result} displayHour={displayHour} />

      <p className="text-xs text-gray-500">
        Errors use the median forecast (q50) from the latest issue per delivery hour{shifted ? '; "With shifts" adds the enabled downshift and upshift to it, "No shifts" scores it as issued' : ''}.
        Hourly scores use every hour with both a forecast and a Fix II price; daily scores need all delivery hours. Positive bias means over-prediction.
        The TGE BASE comparison and the direction use the Fix I daily average instead.
      </p>
    </>
  );
};

const Diagnosis = () => {
  const { fetchPost } = useApi();

  const [form, setForm] = useState(() => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return {
      start_date: `${today.getFullYear()}-01-01`,
      end_date: formatDate(tomorrow),
      model: 'v30-k',
      apply_risk_score: true,
      risk_score_model: 'prod-v2.0.0',
      apply_spike_risk: false,
      spike_risk_model: PAIRED_UPSHIFT['v30-k'],
    };
  });
  const [addOneHour, setAddOneHour] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // { data, form, id }: the response, the inputs that produced it, and a run counter.
  const [run, setRun] = useState(null);

  const update = (field, value) => setForm(current => ({ ...current, [field]: value }));
  const selectModel = (model) => setForm(current => ({
    ...current,
    model,
    spike_risk_model: PAIRED_UPSHIFT[model] || current.spike_risk_model,
  }));
  const displayHour = (hour) => (addOneHour ? hour + 1 : hour);

  const runAnalysis = async () => {
    if (form.start_date > form.end_date) {
      setError('End date must be on or after start date.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const body = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        // A switched-off shift sends no model, which the backend reads as off.
        if (key === 'risk_score_model' && !form.apply_risk_score) return;
        if (key === 'spike_risk_model' && !form.apply_spike_risk) return;
        body.append(key, value);
      });
      const response = await fetchPost('/diagnosis', body);
      const json = await response.json();
      if (json.status === 'success') {
        setRun(previous => ({ data: json, form, id: (previous?.id || 0) + 1 }));
      } else {
        setError(json.message || 'Diagnosis failed');
      }
    } catch (err) {
      console.error('Error running diagnosis:', err);
      setError('Network error. Failed to run diagnosis.');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent';
  const labelClass = 'flex items-center space-x-2 text-sm font-medium mb-2';
  const result = run?.data;
  const changed = run && Object.keys(form).some(key => form[key] !== run.form[key]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center space-x-2">
            <Activity className="w-6 h-6 text-blue-600" />
            <span>Diagnosis</span>
          </h2>
          <p className="text-gray-600 mt-1">Forecast errors, direction and uncertainty against Fix II</p>
        </div>
        <span className="text-sm text-gray-500">Europe/Warsaw · PLN/MWh</span>
      </div>

      {/* Controls */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-7 gap-4">
          <div>
            <label className={`${labelClass} text-gray-700`}><Calendar size={16} /><span>Start Date</span></label>
            <input type="date" value={form.start_date} onChange={(e) => update('start_date', e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className={`${labelClass} text-gray-700`}><Calendar size={16} /><span>End Date</span></label>
            <input type="date" value={form.end_date} onChange={(e) => update('end_date', e.target.value)} className={inputClass} />
          </div>
          <div className="xl:col-span-2">
            <label className={`${labelClass} text-gray-700`}><TrendingUp size={16} /><span>Model</span></label>
            <select value={form.model} onChange={(e) => selectModel(e.target.value)} className={inputClass}>
              {MODELS.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className={`${labelClass} ${form.apply_risk_score ? 'text-gray-700' : 'text-gray-400'}`}><AlertCircle size={16} /><span>Downshift Model</span></label>
            <select
              value={form.risk_score_model}
              onChange={(e) => update('risk_score_model', e.target.value)}
              disabled={!form.apply_risk_score}
              title={form.apply_risk_score ? 'Price Collapse Risk Engine' : 'Enable Apply Downshift to choose a model'}
              className={`${inputClass} ${form.apply_risk_score ? '' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}
            >
              {COLLAPSE_MODELS.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className={`${labelClass} ${form.apply_spike_risk ? 'text-gray-700' : 'text-gray-400'}`}><AlertCircle size={16} /><span>Upshift Model</span></label>
            <select
              value={form.spike_risk_model}
              onChange={(e) => update('spike_risk_model', e.target.value)}
              disabled={!form.apply_spike_risk}
              title={form.apply_spike_risk ? 'Price Spike Risk Engine' : 'Enable Apply Upshift to choose a model'}
              className={`${inputClass} ${form.apply_spike_risk ? '' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}
            >
              {SPIKE_MODELS.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={runAnalysis}
              disabled={loading}
              className={`w-full flex items-center justify-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                loading ? 'bg-gray-400 text-white cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {loading ? <Loader className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              <span>{loading ? 'Running…' : 'Run Analysis'}</span>
            </button>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2">
          <Toggle label="Apply Downshift" checked={form.apply_risk_score} onChange={(v) => update('apply_risk_score', v)} />
          <Toggle label="Apply Upshift" checked={form.apply_spike_risk} onChange={(v) => update('apply_spike_risk', v)} />
          <Toggle label="Display hours as 1-24" checked={addOneHour} onChange={setAddOneHour} />
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
          <div>
            <p className="text-red-800 font-medium">Diagnosis failed</p>
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        </div>
      )}

      {!result && !loading && !error && (
        <div className="text-center py-12 text-gray-500">
          <Activity className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p>Choose a model and a period, then run the analysis</p>
        </div>
      )}

      {!result && loading && (
        <div className="text-center py-12 text-gray-500">
          <Loader className="w-8 h-8 mx-auto mb-3 animate-spin" />
          <p>Running diagnosis...</p>
        </div>
      )}

      {result && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-gray-600">
            <span className="inline-flex items-center gap-1.5 font-medium text-gray-800">
              <CheckCircle2 size={15} className="text-green-600" />
              {result.parameters.model} · {result.parameters.start_date} → {result.parameters.end_date}
            </span>
            <span>
              Downshift: {result.parameters.risk_score_model || 'off'} · Upshift: {result.parameters.spike_risk_model || 'off'} · scored against Fix II
            </span>
          </div>
          {changed && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              Parameters have changed. Run Analysis to update the displayed results.
            </div>
          )}
          <DiagnosisResults key={run.id} result={result} displayHour={displayHour} />
        </>
      )}
    </div>
  );
};

export default Diagnosis;
